// @ts-strict-ignore
import { dayjs } from '@/extendedDayjs';
import {
    AllReleaseStepTypes,
    AutomationStatus,
    ReleaseStepType,
    TimelineEvent,
    TimelineEventBatchActionRequestedData,
    TimelineEventStatus,
    TimelineEventType,
} from '../../types';
import { Nullable } from '@/typescriptHelpers';

export const PHASED_RELEASE_TIMELINE_EVENT_TYPES = [
    TimelineEventType.AppStoreVersionPhasedReleaseUpdated,
    TimelineEventType.AppStoreVersionPhasedReleaseHalted,
    TimelineEventType.AppStoreVersionPhasedReleaseResumed,
];

export const TIMELINE_EVENT_TYPES_BY_RELEASE_STEP_TYPE: Record<ReleaseStepType, TimelineEventType[]> = {
    [ReleaseStepType.Kickoff]: [
        TimelineEventType.ReleaseCreated,
        TimelineEventType.ReleaseCreatedUpcomingRelease,
        TimelineEventType.ReleaseVersionBumped,
        TimelineEventType.ReleaseBranchCreated,
        TimelineEventType.ReleaseKickedOff,
        TimelineEventType.ReleaseCodePromoted,
        TimelineEventType.ReleaseTargetKickoffDateUpdated,
        TimelineEventType.ReleaseTargetSubmissionDateUpdated,
        TimelineEventType.ReleaseTargetReleaseDateUpdated,
        TimelineEventType.ReleaseVersionUpdated,
        TimelineEventType.ReleasePilotChanged,
        TimelineEventType.TargetKickoffDateUpcoming,
        TimelineEventType.AutoCreateRunwayReleasesUpcoming,
        TimelineEventType.ReleaseSchedulePaused,
        TimelineEventType.ReleaseScheduleResumed,
        TimelineEventType.FeatureFlagToggled,
    ],
    [ReleaseStepType.Submission]: [
        TimelineEventType.ReviewSubmissionItemAdded,
        TimelineEventType.ReviewSubmissionItemRemoved,
        TimelineEventType.ReviewSubmissionItemStatusUpdated,
        TimelineEventType.ReleaseSubmitted,
        TimelineEventType.ReleaseStepsReadyToSubmit,
        TimelineEventType.ReleaseTargetSubmissionDateUpdated,
        TimelineEventType.AppStoreVersionStatusUpdated,
        TimelineEventType.AppStoreVersionAppStoreBuildChanged,
        TimelineEventType.TargetSubmissionDateUpcoming,
        TimelineEventType.ReleaseSchedulePaused,
        TimelineEventType.ReleaseScheduleResumed,
        TimelineEventType.ReviewDetailsUpdated,
        TimelineEventType.ReviewAttachmentUpdated,
        TimelineEventType.ReviewAttachmentRemoved,
    ],
    [ReleaseStepType.Takeoff]: [
        TimelineEventType.ReviewSubmissionItemStatusUpdated,
        TimelineEventType.ReleaseReleased,
        TimelineEventType.ReleaseTagged,
        TimelineEventType.ReleaseStepsReadyToRelease,
        TimelineEventType.ReleaseTargetReleaseDateUpdated,
        TimelineEventType.ReleaseVersionUpdated,
        TimelineEventType.AppStoreVersionStatusUpdated,
        TimelineEventType.AppStoreVersionPhasedReleaseUpdated,
        TimelineEventType.AppStoreVersionPhasedReleaseHalted,
        TimelineEventType.AppStoreVersionPhasedReleaseResumed,
        TimelineEventType.AppStoreVersionReleaseNotesUpdated,
        TimelineEventType.TargetReleaseDateUpcoming,
        TimelineEventType.ReleaseSchedulePaused,
        TimelineEventType.ReleaseScheduleResumed,
        TimelineEventType.VCSReleaseAssetsUploaded,
    ],
    [ReleaseStepType.ReleaseCandidate]: [
        TimelineEventType.ReleaseStepRCBuildStatusChanged,
        TimelineEventType.ReleaseStepCIBuildFailureOverridden,
        TimelineEventType.BinaryArtifactAppStoreUploadFailed,
        TimelineEventType.BinaryArtifactAppStoreUploadSuccess,
        TimelineEventType.DsymArtifactStabilityMonitoringUploadFailed,
        TimelineEventType.DsymArtifactStabilityMonitoringUploadSuccess,
    ],
    [ReleaseStepType.RegressionTesting]: [TimelineEventType.ReleaseStepRegressionTestingStatusChanged],
    [ReleaseStepType.BetaTesting]: [
        TimelineEventType.ReleaseStepBetaSoakStarted,
        TimelineEventType.ReleaseStepBetaSoakFinished,
        TimelineEventType.AppStoreVersionBetaSubmittedForReview,
    ],
    [ReleaseStepType.Metadata]: [
        TimelineEventType.ReleaseStepMetadataApprovalChanged,
        TimelineEventType.AppStoreVersionReleaseNotesUpdated,
        TimelineEventType.MetadataTranslationsExported,
        TimelineEventType.MetadataTranslationsUploaded,
        TimelineEventType.SyncedVCSMetadata,
        TimelineEventType.CommittedVCSMetadata,
    ],
    [ReleaseStepType.Translations]: [
        TimelineEventType.ReleaseStepTranslationsApprovalChanged,
        TimelineEventType.TranslationsUploaded,
        TimelineEventType.TranslationsExported,
        TimelineEventType.TranslationsUpdated,
    ],
    [ReleaseStepType.Screenshots]: [
        TimelineEventType.ReleaseStepScreenshotsApprovalChanged,
        TimelineEventType.ReleaseStepScreenshotAdded,
        TimelineEventType.ReleaseStepStagedScreenshotAdded,
        TimelineEventType.ReleaseStepScreenshotDeleted,
        TimelineEventType.ReleaseStepScreenshotsReordered,
        TimelineEventType.ReleaseStepStagedScreenshotsApplied,
        TimelineEventType.RunwayEventReleaseStepAppPreviewAdded,
        TimelineEventType.RunwayEventReleaseStepAppPreviewDeleted,
    ],
    [ReleaseStepType.FeatureReadiness]: [TimelineEventType.VCSCherryPickConflict],
    [ReleaseStepType.Approvals]: [],
    [ReleaseStepType.StoreReview]: [
        TimelineEventType.ReleaseSubmitted,
        TimelineEventType.ReleaseMarkedRejected,
        TimelineEventType.AppStoreVersionStatusUpdated,
        TimelineEventType.ReviewSubmissionItemStatusUpdated,
    ],
    [ReleaseStepType.CIDistribution]: [],
    [ReleaseStepType.VCSDistribution]: [],
    [ReleaseStepType.RollbackBuild]: [
        TimelineEventType.ResignSequenceStarted,
        TimelineEventType.ResignSequenceCompleted,
        TimelineEventType.ResignSequenceSkipped,
        TimelineEventType.BuildResignStarted,
        TimelineEventType.BuildResignCompleted,
    ],
    [ReleaseStepType.Rollout]: [],
    [ReleaseStepType.ReleaseCycle]: [],
};

export const RELEASE_STEP_TYPE_BY_EVENT_TYPE: Record<TimelineEventType, ReleaseStepType> = AllReleaseStepTypes.reduce(
    (acc, releaseStepType) => {
        const eventTypes = TIMELINE_EVENT_TYPES_BY_RELEASE_STEP_TYPE[releaseStepType];
        eventTypes.forEach((eventType) => {
            acc[eventType] = releaseStepType;
        });
        return acc;
    },
    {} as Record<TimelineEventType, ReleaseStepType>,
);

const PRIORITY_ORDER = [
    TimelineEventType.ReleaseStepRegressionTestingStatusChanged,
    TimelineEventType.ReleaseStepStatusChanged,
    TimelineEventType.ReleaseStepChecklistCompleted,
    TimelineEventType.ChecklistItemStatusUpdated,
];

export const sortTimelineEvents = (events: TimelineEvent[] = []) => {
    return [...events].sort((a, b) => {
        const diffInMilliseconds = dayjs(b.triggeredAt).diff(dayjs(a.triggeredAt));
        const parallelEventsThresholdInMilliseconds = 10 * 1000;
        if (Math.abs(diffInMilliseconds) <= parallelEventsThresholdInMilliseconds) {
            const indexOfA = PRIORITY_ORDER.indexOf(a.type);
            const indexOfB = PRIORITY_ORDER.indexOf(b.type);

            // to handle events that are not in the PRIORITY_ORDER
            if (indexOfA === -1 && indexOfB === -1) {
                return 0;
            } else if (indexOfA === -1) {
                return 1;
            } else if (indexOfB === -1) {
                return -1;
            }

            return indexOfA - indexOfB;
        }

        return diffInMilliseconds;
    });
};

export const batchActionSummaryInfosFromBatchTimelineEventData = (
    data: Nullable<TimelineEventBatchActionRequestedData>,
) => {
    const lastAttemptedAt = data?.lastAttemptedAt;

    const maxAttempts = data?.maxAttempts;

    const targetStepDisplayNames = data?.targetStepDisplayNames;
    const successesStepDisplayNames = data?.successesStepDisplayNames;
    const pendingRetryStepDisplayNames = data?.pendingRetryStepDisplayNames;
    const failuresStepDisplayNames = data?.failuresStepDisplayNames;

    const successfulForStepRunIds = data?.succeededForStepRunIds;
    const pendingRetryForStepRunIds = data?.pendingRetryForStepRunIds;
    const failedForStepRunIds = data?.failedForStepRunIds;

    const successfulForDestStepConfigIds = data?.successesDestStepConfigIds;
    const pendingRetryForDestStepConfigIds = data?.pendingRetryDestStepConfigIds;
    const failedForDestStepConfigIds = data?.failuresDestStepConfigIds;

    const anySuccessful = successfulForStepRunIds?.length > 0;
    const anyPendingRetry = pendingRetryForStepRunIds?.length > 0;
    const anyFailed = failedForStepRunIds?.length > 0;

    const retryExpected = anyPendingRetry;

    return {
        lastAttemptedAt,
        maxAttempts,
        targetStepDisplayNames,
        successesStepDisplayNames,
        pendingRetryStepDisplayNames,
        failuresStepDisplayNames,
        successfulForStepRunIds,
        pendingRetryForStepRunIds,
        failedForStepRunIds,
        successfulForDestStepConfigIds,
        pendingRetryForDestStepConfigIds,
        failedForDestStepConfigIds,
        anySuccessful,
        anyPendingRetry,
        anyFailed,
        retryExpected,
    };
};

export const getTimelineEventsByStatus = (
    timelineEvents: TimelineEvent[] = [],
    shouldIncludeActiveEvents?: boolean,
): {
    recurringEvents: TimelineEvent[];
    upcomingEvents: TimelineEvent[];
    upcomingAndTargetEvents: TimelineEvent[];
    pastEvents: TimelineEvent[];
} => {
    const failedOrSkippedRecurringAutomationTypes = {};
    const upcomingEvents: TimelineEvent[] = [];
    const upcomingAndTargetEvents: TimelineEvent[] = [];
    const pastEvents: TimelineEvent[] = [];
    var recurringEvents: TimelineEvent[] = [];

    for (const event of timelineEvents) {
        if (event.status === TimelineEventStatus.Upcoming) {
            upcomingEvents.push(event);
            upcomingAndTargetEvents.push(event);
            continue;
        }

        if (event.status === TimelineEventStatus.Target) {
            upcomingAndTargetEvents.push(event);
            continue;
        }

        if (event.status === TimelineEventStatus.Recurring) {
            if (
                event.automationData?.automationStatus === AutomationStatus.Failed ||
                event.automationData?.automationStatus === AutomationStatus.Pending
            ) {
                failedOrSkippedRecurringAutomationTypes[event.automationData.automationType] = true;
            } else if (
                !shouldIncludeActiveEvents &&
                event.automationData?.automationStatus === AutomationStatus.Active
            ) {
                continue;
            }

            recurringEvents.push(event);
            continue;
        }

        pastEvents.push(event);
    }

    // Removing placeholder recurring events when we have active failing recurring events
    if (shouldIncludeActiveEvents) {
        recurringEvents = recurringEvents.filter(
            (event) =>
                !(
                    event.automationData?.automationStatus === AutomationStatus.Active &&
                    failedOrSkippedRecurringAutomationTypes[event.automationData.automationType]
                ),
        );
    }

    upcomingEvents.sort((a, b) => {
        const aIsChecklistItem = a.type === TimelineEventType.ChecklistItemDueUpcoming;
        const bIsChecklistItem = b.type === TimelineEventType.ChecklistItemDueUpcoming;

        if (aIsChecklistItem !== bIsChecklistItem) {
            return aIsChecklistItem ? 1 : -1;
        }
        if (aIsChecklistItem) {
            return dayjs(a.triggeredAt).valueOf() - dayjs(b.triggeredAt).valueOf();
        }
        return (a.releaseStepArchetypes?.[0] ?? '').localeCompare(b.releaseStepArchetypes?.[0] ?? '');
    });

    return {
        recurringEvents,
        upcomingEvents,
        pastEvents,
        upcomingAndTargetEvents,
    };
};

const ATTENTION_AUTOMATION_STATUSES = new Set<AutomationStatus>([AutomationStatus.Failed, AutomationStatus.Blocked]);

export const getTimelineEventsNeedingAttention = (timelineEvents: TimelineEvent[] = []): TimelineEvent[] => {
    const { upcomingEvents, recurringEvents } = getTimelineEventsByStatus(timelineEvents, true);

    return [...upcomingEvents, ...recurringEvents].filter(
        (event) =>
            event.automationData?.automationStatus &&
            ATTENTION_AUTOMATION_STATUSES.has(event.automationData.automationStatus),
    );
};

export const getTimelineAttentionFingerprint = (attentionEvents: TimelineEvent[]): string =>
    attentionEvents
        .map(
            (event) =>
                `${event.automationData?.automationId ?? event.type}:${event.automationData?.automationStatus}:${event.automationData?.failureReason ?? ''}`,
        )
        .sort()
        .join('|');
